import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
    Box, 
    Typography, 
    Paper, 
    Chip, 
    Stack, 
    CircularProgress, 
    TextField, 
    MenuItem, 
    Grid, 
    TablePagination,
    InputAdornment,
    Button
} from '@mui/material';
import {
    ShoppingCart,
    Payments,
    ReceiptLong,
    History,
    Refresh,
    Search as SearchIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr } from 'date-fns/locale';
import { fetchBillets } from '../../data/slices/billetsSlice';
import { fetchPaiments } from '../../data/slices/paiementsSlice';
import facturationService from '../../services/facturationService';
import UpdatedTable from '../../components/updated-table/StyledTable';
import { COLORS, dateFormatter } from '../../settings/themeSettings';
import PageHeader from '../../components/layout/PageHeader';

/**
 * Journal des Événements - Chronologie Unifiée
 * Regroupe les ventes, les paiements et les factures par ordre chronologique.
 */
const JournalEvenements = () => {
    const dispatch = useDispatch();

    // Données Redux
    const { billets, fetchBilletsStatus } = useSelector((state) => state.billets);
    const { paiements, fetchPaiementStatus } = useSelector((state) => state.paiements);

    // Données locales pour les factures globales
    const [factures, setFactures] = useState([]);
    const [facturesLoading, setFacturesLoading] = useState(false);

    // États pour les filtres et la pagination
    const [filters, setFilters] = useState({
        search: '',
        type: 'ALL',
        dateDebut: null,
        dateFin: null
    });

    const [pagination, setPagination] = useState({
        page: 0,
        rowsPerPage: 25
    });

    // Chargement initial
    useEffect(() => {
        dispatch(fetchBillets());
        dispatch(fetchPaiments());
        loadInvoices();
    }, [dispatch]);

    const loadInvoices = async () => {
        try {
            setFacturesLoading(true);
            const response = await facturationService.getFactures(1, 200); 
            const data = Array.isArray(response) ? response : (response.data || []);
            setFactures(data);
        } catch (error) {
            console.error('Erreur chargement factures chronologie:', error);
        } finally {
            setFacturesLoading(false);
        }
    };

    // Fusion et Normalisation des données
    const allEvents = useMemo(() => {
        const events = [];

        // 1. Normaliser les Billets (Ventes)
        if (Array.isArray(billets)) {
            billets.forEach(b => {
                const rawValue = b.createdAt || b.date_emission || b.date_creation || b.dateVol;
                const d = rawValue ? new Date(rawValue) : new Date();
                const isValid = !isNaN(d.getTime());

                events.push({
                    id: `vente-${b.id}`,
                    dateRaw: isValid ? d : new Date(),
                    date: dateFormatter(rawValue) || '--',
                    heure: (isValid && (d.getHours() !== 0 || d.getMinutes() !== 0))
                        ? d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                        : (b.heure || b.heure_emission || '--'),
                    type: 'VENTE',
                    description: `Billet #${b.numero} - ${b.nom || 'Client'} (${b.trajet || 'N/A'})`,
                    montant: Number(b.tarif) || 0,
                    status: b.etat || 'Émis',
                    user: b.agent || 'Système',
                    icon: <ShoppingCart fontSize="small" />,
                    color: 'info'
                });
            });
        }

        // 2. Normaliser les Paiements
        if (Array.isArray(paiements)) {
            paiements.forEach(p => {
                const rawDate = p.date || p.createdAt;
                const d = rawDate ? new Date(rawDate) : new Date();
                const isValid = !isNaN(d.getTime());

                events.push({
                    id: `paiement-${p.id}`,
                    dateRaw: isValid ? d : new Date(),
                    date: dateFormatter(rawDate) || '--',
                    heure: (isValid && (d.getHours() !== 0 || d.getMinutes() !== 0))
                        ? d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                        : (p.heure || '--'),
                    type: 'PAIEMENT',
                    description: `Paiement ${p.methode || ''} pour Facture #${p.facture || ''}`,
                    montant: Number(p.montant) || 0,
                    status: 'Reçu',
                    user: p.caissier || 'Système',
                    icon: <Payments fontSize="small" />,
                    color: 'success'
                });
            });
        }

        // 3. Normaliser les Factures
        if (Array.isArray(factures)) {
            factures.forEach(f => {
                const dateRaw = f.createdAt || f.date;
                const d = dateRaw ? new Date(dateRaw) : new Date();
                const isValid = !isNaN(d.getTime());

                events.push({
                    id: `facture-${f.id}`,
                    dateRaw: isValid ? d : new Date(),
                    date: dateFormatter(dateRaw) || '--',
                    heure: (isValid && (d.getHours() !== 0 || d.getMinutes() !== 0))
                        ? d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                        : (f.heure || f.heure_creation || '--'),
                    type: 'FACTURATION',
                    description: `Nouvelle Facture #${f.id} - ${f.client?.libelle || f.client?.nom || 'Client'}`,
                    montant: Number(f.montant) || 0,
                    status: f.statut || 'Générée',
                    user: f.createur || 'Système',
                    icon: <ReceiptLong fontSize="small" />,
                    color: 'warning'
                });
            });
        }

        // Trier par date décroissante
        return events.sort((a, b) => b.dateRaw - a.dateRaw);
    }, [billets, paiements, factures]);

    // Application des filtres
    const filteredEvents = useMemo(() => {
        return allEvents.filter(event => {
            // Filtre par type
            if (filters.type !== 'ALL' && event.type !== filters.type) return false;

            // Filtre par recherche (description ou utilisateur)
            if (filters.search) {
                const searchLower = filters.search.toLowerCase();
                const matchDesc = event.description.toLowerCase().includes(searchLower);
                const matchUser = event.user.toLowerCase().includes(searchLower);
                if (!matchDesc && !matchUser) return false;
            }

            // Filtre par plage de dates
            if (filters.dateDebut && event.dateRaw < filters.dateDebut) return false;
            if (filters.dateFin) {
                const dateFinLimit = new Date(filters.dateFin);
                dateFinLimit.setHours(23, 59, 59, 999);
                if (event.dateRaw > dateFinLimit) return false;
            }

            return true;
        });
    }, [allEvents, filters]);

    // Données paginées
    const paginatedEvents = useMemo(() => {
        const start = pagination.page * pagination.rowsPerPage;
        return filteredEvents.slice(start, start + pagination.rowsPerPage);
    }, [filteredEvents, pagination]);

    const handleFilterChange = (name, value) => {
        setFilters(prev => ({ ...prev, [name]: value }));
        setPagination(prev => ({ ...prev, page: 0 })); 
    };

    const handleChangePage = (event, newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const handleChangeRowsPerPage = (event) => {
        setPagination(prev => ({
            ...prev,
            rowsPerPage: parseInt(event.target.value, 10),
            page: 0
        }));
    };

    const isLoading = fetchBilletsStatus === 'loading' || fetchPaiementStatus === 'loading' || facturesLoading;

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f8fafc', minHeight: '100vh' }}>
            <PageHeader 
                title="Journal des Événements"
                subtitle="Chronologie unifiée de toutes les activités de l'application"
                icon={History}
                breadcrumbs={[
                    { label: 'Journaux', path: '/journal' },
                    { label: 'Événements', path: '/journal/evenements' }
                ]}
                actions={[
                    { 
                        label: 'Actualiser', 
                        icon: <Refresh />, 
                        onClick: () => {
                            dispatch(fetchBillets());
                            dispatch(fetchPaiments());
                            loadInvoices();
                        },
                        variant: 'outlined'
                    }
                ]}
            />

            {/* Section Filtres */}
            <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: '16px', border: '1px solid', borderColor: COLORS.gray[200], bgcolor: 'white' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Rechercher (événement, utilisateur...)"
                            value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Type d'événement"
                            value={filters.type}
                            onChange={(e) => handleFilterChange('type', e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                        >
                            <MenuItem value="ALL">Tous les types</MenuItem>
                            <MenuItem value="VENTE">Ventes (Billets)</MenuItem>
                            <MenuItem value="PAIEMENT">Paiements</MenuItem>
                            <MenuItem value="FACTURATION">Facturation</MenuItem>
                        </TextField>
                    </Grid>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
                        <Grid item xs={12} md={2.5}>
                            <DatePicker
                                label="Date de début"
                                value={filters.dateDebut}
                                onChange={(newValue) => handleFilterChange('dateDebut', newValue)}
                                slotProps={{ textField: { size: 'small', fullWidth: true, sx: { '& .MuiOutlinedInput-root': { borderRadius: '10px' } } } }}
                            />
                        </Grid>
                        <Grid item xs={12} md={2.5}>
                            <DatePicker
                                label="Date de fin"
                                value={filters.dateFin}
                                onChange={(newValue) => handleFilterChange('dateFin', newValue)}
                                slotProps={{ textField: { size: 'small', fullWidth: true, sx: { '& .MuiOutlinedInput-root': { borderRadius: '10px' } } } }}
                            />
                        </Grid>
                    </LocalizationProvider>
                    <Grid item xs={12} md={2}>
                        <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                            <Chip 
                                label={`${filteredEvents.length} évts`} 
                                color="primary" 
                                variant="outlined" 
                                size="small"
                                sx={{ fontWeight: 'bold' }}
                            />
                            <Button 
                                variant="text" 
                                size="small"
                                onClick={() => setFilters({ search: '', type: 'ALL', dateDebut: null, dateFin: null })}
                                sx={{ textTransform: 'none', minWidth: 'auto' }}
                            >
                                Effacer
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            {isLoading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10 }}>
                    <CircularProgress color="primary" />
                    <Typography sx={{ mt: 2, color: COLORS.text.secondary }}>Chargement de l'historique...</Typography>
                </Box>
            ) : (
                <Paper elevation={0} sx={{ 
                    borderRadius: '16px', 
                    overflow: 'hidden', 
                    border: '1px solid',
                    borderColor: COLORS.gray[200],
                    bgcolor: 'white'
                }}>
                    <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: COLORS.gray[100], bgcolor: COLORS.gray[50] }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: COLORS.secondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Flux chronologique des activités
                        </Typography>
                    </Box>
                    
                    <UpdatedTable
                        rows={paginatedEvents}
                        head={['date', 'heure', 'type', 'description', 'montant', 'status']}
                        headSubs={{
                            "date": "Date",
                            "heure": "Heure",
                            "type": "Type",
                            "description": "Événement / Détails",
                            "montant": "Montant (FCFA)",
                            "status": "État"
                        }}
                        totals={['montant']}
                    />
                    
                    <Box sx={{ borderTop: '1px solid #e5e7eb', px: 2, py: 1, display: 'flex', justifyContent: 'flex-end', bgcolor: COLORS.gray[50] }}>
                        <TablePagination
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            component="div"
                            count={filteredEvents.length}
                            rowsPerPage={pagination.rowsPerPage}
                            page={pagination.page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            labelRowsPerPage="Lignes :"
                            labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
                        />
                    </Box>
                </Paper>
            )}
        </Box>
    );
};

export default JournalEvenements;
